import type { AsyncState } from './types';

/** Boolean flags for each check the guard can perform. Only set to true what the applet requires. */
export type Policy = {
  /**
   * When true, anonymous users see the access-denied screen instead of the applet.
   * When false/omitted with `requiresApiKey: 'soft'`, anonymous users see the applet plus a sign-in modal.
   */
  requiresLoggedIn?: boolean;
  /**
   * `true` — block the applet UI without an API key.
   * `'soft'` — allow the applet UI; logged-in users without a key see an upgrade modal; anonymous users
   * see a sign-in modal when `requiresLoggedIn` is not true.
   */
  requiresApiKey?: boolean | 'soft';
  requiresAdmin?: boolean;
};

export type AppletPermissionContext = {
  authStatus: 'anonymous' | 'user';
  apiKey: AsyncState<{ apiKey: string; serviceID: string }>;
  isCartwheeler: boolean;
  /** True while userStore is loading (e.g. after login, before userInfo/isCartwheeler are set). */
  userStoreLoading: boolean;
  /** Used to tell whether an API key was ever fetchable (users without an org never get a key). */
  userOrganizationId: string;
};

/** Canonical values for `AppletAccessDenialReason` — use these instead of string literals at call sites. */
export const APPLET_ACCESS_DENIAL_REASON = {
  anonymous: 'anonymous',
  noApiKey: 'noApiKey',
  notAdmin: 'notAdmin',
} as const;

export type AppletAccessDenialReason =
  (typeof APPLET_ACCESS_DENIAL_REASON)[keyof typeof APPLET_ACCESS_DENIAL_REASON];

export type AppletAccessResult =
  | true
  | 'loading'
  | { allowed: false; reason: AppletAccessDenialReason }
  | { allowed: true; warnNoApiKey: true }
  /** Logged-out preview: show applet UI with a sign-in prompt (soft API-key applets only). */
  | { allowed: true; warnSignIn: true };

type ApiKeyGateState = 'loading' | 'missing' | 'present';

function getApiKeyGateState(ctx: AppletPermissionContext): ApiKeyGateState {
  const orgId = ctx.userOrganizationId?.trim();
  if (!orgId) {
    return 'missing';
  }
  const { status, data } = ctx.apiKey;
  if ((status === 'initial' || status === 'pending') && !data) {
    return 'loading';
  }
  if (status === 'error' || (status === 'ready' && !data)) {
    return 'missing';
  }
  return 'present';
}

/** True if the user can open the applet route (including soft API-key warning). */
export function appletEntryIsAllowed(access: AppletAccessResult): boolean {
  if (access === true) return true;
  if (access === 'loading') return false;
  if (typeof access === 'object' && access.allowed === true) return true;
  return false;
}

export function isAppletAccessLoading(
  access: AppletAccessResult,
): access is 'loading' {
  return access === 'loading';
}

export function isAppletAccessFullAllow(
  access: AppletAccessResult,
): access is true {
  return access === true;
}

export function isAppletAccessDenied(
  access: AppletAccessResult,
): access is { allowed: false; reason: AppletAccessDenialReason } {
  return typeof access === 'object' && access.allowed === false;
}

export function isAppletAccessDeniedForReason(
  access: AppletAccessResult,
  reason: AppletAccessDenialReason,
): access is { allowed: false; reason: AppletAccessDenialReason } {
  return isAppletAccessDenied(access) && access.reason === reason;
}

export function isAppletAccessWarnNoApiKey(
  access: AppletAccessResult,
): access is { allowed: true; warnNoApiKey: true } {
  return (
    typeof access === 'object' &&
    access.allowed === true &&
    'warnNoApiKey' in access &&
    access.warnNoApiKey === true
  );
}

export function isAppletAccessWarnSignIn(
  access: AppletAccessResult,
): access is { allowed: true; warnSignIn: true } {
  return (
    typeof access === 'object' &&
    access.allowed === true &&
    'warnSignIn' in access &&
    access.warnSignIn === true
  );
}

/** Allowed applet UI with either soft API-key or sign-in prompt (mutually exclusive in practice). */
export function isAppletAccessAllowedWithUiWarning(
  access: AppletAccessResult,
): access is
  | { allowed: true; warnNoApiKey: true }
  | { allowed: true; warnSignIn: true } {
  return isAppletAccessWarnNoApiKey(access) || isAppletAccessWarnSignIn(access);
}

export function evaluatePolicy(
  policy: Policy,
  ctx: AppletPermissionContext,
): AppletAccessResult {
  const blockAnonymousAtDoor =
    policy.requiresLoggedIn === true ||
    policy.requiresAdmin === true ||
    policy.requiresApiKey === true;

  if (blockAnonymousAtDoor && ctx.authStatus === 'anonymous') {
    return { allowed: false, reason: APPLET_ACCESS_DENIAL_REASON.anonymous };
  }

  const apiKeyMode = policy.requiresApiKey;
  const needsUserStore =
    (apiKeyMode !== undefined || policy.requiresAdmin) &&
    ctx.authStatus === 'user';

  if (needsUserStore && ctx.userStoreLoading) {
    return 'loading';
  }

  let softNoApiKey = false;

  if (apiKeyMode === true) {
    const gate = getApiKeyGateState(ctx);
    if (gate === 'loading') {
      return 'loading';
    }
    if (gate === 'missing') {
      return { allowed: false, reason: APPLET_ACCESS_DENIAL_REASON.noApiKey };
    }
  } else if (apiKeyMode === 'soft') {
    const gate = getApiKeyGateState(ctx);
    if (gate === 'loading') {
      return 'loading';
    }
    if (gate === 'missing') {
      if (ctx.authStatus === 'anonymous') {
        return { allowed: true, warnSignIn: true };
      }
      softNoApiKey = true;
    }
  }

  if (policy.requiresAdmin && !ctx.isCartwheeler) {
    return { allowed: false, reason: APPLET_ACCESS_DENIAL_REASON.notAdmin };
  }

  if (softNoApiKey) {
    return { allowed: true, warnNoApiKey: true };
  }

  return true;
}
